/**
 * Hooks pour la recuperation des donnees publiques d'un etablissement
 *
 * Fournit une interface pour recuperer les informations publiques
 * avec support PWA et revalidation automatique.
 *
 * @module PublicCompanyHooks
 * @author Equipe Brydge
 * @since 2.0.0
 */

import {useMemo} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';

/**
 * Recupere les donnees publiques d'un etablissement avec revalidation automatique
 *
 * Ce hook garantit que les donnees sont toujours fraiches :
 * - Revalidation au retour sur l'application (PWA iOS/Android)
 * - Revalidation au focus et a la reconnexion reseau
 *
 * @param {string} slug - Slug de l'etablissement
 * @return {Object} {companyDetails, isLoading, isValidating, isError, refresh}
 */
export const useGetCompanyDetails = (slug) => {
  const endpointUrl = slug ? `${endpoints.public.company.details}/${slug}` : null;

  const {data, error, isValidating, mutate} = useSWRWithSync(
      endpointUrl,
      (url) => axiosInstance.get(url).then((res) => res.data),
      {
        swrOptions: {
          shouldRetryOnError: false,
        },
      },
  );

  const memoizedData = useMemo(() => data, [data]);

  return {
    companyDetails: memoizedData,
    isLoading: !error && !data && isValidating,
    isError: !!error,
    isValidating,
    refresh: mutate,
  };
};
